import { useCallback, useEffect, useRef } from 'react'

import type { ActivationGroup } from '@/features/activationGroup/types'
import { useMultimarketValidateRegulationObject } from '@/features/bidding/hooks/useValidateRegulationObject'
import type { ApiValidateConflictsPayload } from '@/features/bidding/types/api'
import type { ExtendedBid } from '@/features/bidding/types/bid'

const extendedBidsToConflictsPayload = (extendedBids: ExtendedBid[]): ApiValidateConflictsPayload => {
  return extendedBids.map((bid) => {
    return {
      context: {
        portfolio: bid.portfolio,
        deliveryDay: bid.deliveryDay.toISODate(),
        marketProgram: bid.marketProgram,
        activationGroupUuid: bid.activationGroupUuid,
        bidType: bid.bidType,
      },
      offeredValues: bid.ptus.map((ptu) => ({
        ptu: ptu.ptu,
        volume: ptu.offeredVolume,
        ptuChunks: ptu.ptuChunks,
      })),
      status: bid.status,
    }
  })
}

type UseBidConflictsValidationsProps = {
  activationGroupsMap: Map<string, ActivationGroup>
  upBids: ExtendedBid[]
  downBids: ExtendedBid[]
}

export const useBidConflictsValidations = ({
  activationGroupsMap,
  upBids,
  downBids,
}: UseBidConflictsValidationsProps) => {
  const { validate, isValidating } = useMultimarketValidateRegulationObject({
    eligibleActivationGroups: activationGroupsMap.values().toArray() || [],
    onAcceptConflicts: () => {},
  })

  // Tracks whether the validation effect has fired at least once.
  // Starts true so the button is disabled on the first render (before the effect runs).
  // Set to false once the effect fires and the mutation's isPending takes over.
  const initialValidationPendingRef = useRef(true)

  const validateConflictsAndShowWarningDialog = useCallback(async () => {
    const upBidsPayload = extendedBidsToConflictsPayload(upBids)
    const downBidsPayload = extendedBidsToConflictsPayload(downBids)
    validate([upBidsPayload, downBidsPayload]).catch(() => console.error('Bids have conflicts'))
  }, [validate, upBids, downBids])

  useEffect(() => {
    initialValidationPendingRef.current = false
    validateConflictsAndShowWarningDialog()
  }, [validateConflictsAndShowWarningDialog])

  return { isValidating: initialValidationPendingRef.current || isValidating }
}
